/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { apiBaseUrl } from '../../../../util/api';
import { xenforoDirectMessagesInboxUrl } from '../../../../util/xenforoUrls';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import {
  formatForumUnreadBadge,
  forumMessagesAriaLabel,
} from '../ForumMessagesLink/ForumMessagesLink';
import {
  DrawerAccountSettingsIcon,
  DrawerDirectoryIcon,
  DrawerHomeIcon,
  DrawerListingsIcon,
  DrawerProfileSettingsIcon,
  DrawerTransactionsIcon,
  DrawerWatchedIcon,
  EnvelopeIcon,
} from '../MobileBottomNav/MobileBottomNavIcons';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab =>
      (hasPageName('InboxPage') || hasPageName('TransactionsPage')) &&
      currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    const { name, params, to } = route || {};
    return (
      <li className={classNames(css.row, getCurrentPageClass(name))}>
        <NamedLink name={name} params={params} to={to} className={css.rowLink}>
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.row}>
      <ExternalLink href={href} className={css.rowLink}>
        {text}
      </ExternalLink>
    </li>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {number} [props.forumUnreadCount]
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @param {Function} props.onLogout
 * @returns {JSX.Element}
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    forumUnreadCount = 0,
    customLinks = [],
    onLogout,
    showCreateListingsLink,
  } = props;
  const intl = useIntl();

  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const customLinksSectionMaybe =
    extraLinks.length > 0 ? (
      <div className={css.group}>
        <h2 className={css.groupLabel}>
          <FormattedMessage id="TopbarMobileMenu.moreGroup" />
        </h2>
        <ul className={css.rows}>{extraLinks}</ul>
      </div>
    ) : null;

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isTransactionsPage =
      currentPage?.indexOf('TransactionsPage') === 0 && page?.indexOf('TransactionsPage') === 0;
    const isHomePage =
      page === 'SearchPage' &&
      ['LandingPage', 'SearchPage', 'SearchPageWithListingType'].includes(currentPage);
    const isSellerDirectoryPage =
      page === 'SellerDirectoryPage' && currentPage === 'SellerDirectoryPage';
    return currentPage === page ||
      isAccountSettingsPage ||
      isTransactionsPage ||
      isHomePage ||
      isSellerDirectoryPage
      ? css.currentPage
      : null;
  };

  if (!isAuthenticated) {
    const loginWithR2R = (
      <a href={`${apiBaseUrl()}/api/auth/xenforo`} className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginWithR2R" />
      </a>
    );
    return (
      <nav className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreetingR2R"
              values={{ lineBreak: <br />, loginWithR2R }}
            />
          </div>
          <ul className={css.rows}>
            <li className={css.row}>
              <NamedLink name="SearchPage" className={css.rowLink}>
                <span className={css.rowIcon}>
                  <DrawerHomeIcon />
                </span>
                <FormattedMessage id="TopbarMobileMenu.homeLink" />
              </NamedLink>
            </li>
            <li className={classNames(css.row, currentPageClass('SellerDirectoryPage'))}>
              <NamedLink name="SellerDirectoryPage" className={css.rowLink}>
                <span className={css.rowIcon}>
                  <DrawerDirectoryIcon />
                </span>
                <FormattedMessage id="TopbarMobileMenu.directoryLink" />
              </NamedLink>
            </li>
          </ul>
          {customLinksSectionMaybe}
        </div>
        {createListingsLinkMaybe ? (
          <div className={css.footer}>{createListingsLinkMaybe}</div>
        ) : null}
      </nav>
    );
  }

  const forumUnreadBadgeText = formatForumUnreadBadge(forumUnreadCount);
  const forumMessagesLabel = forumMessagesAriaLabel(intl, forumUnreadCount);
  const forumUnreadCountBadge = forumUnreadBadgeText ? (
    <NotificationBadge className={css.notificationBadge} count={forumUnreadBadgeText} />
  ) : null;

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.displayName || user.attributes.profile.firstName;
  const userUUID = user?.id?.uuid;

  return (
    <div className={css.root}>
      <div className={css.header}>
        <AvatarLarge className={css.avatar} user={currentUser} disableProfileLink />
        <div className={css.headerText}>
          <span className={css.username}>{displayName}</span>
          {userUUID ? (
            <NamedLink className={css.viewProfileLink} name="ProfilePage" params={{ id: userUUID }}>
              <FormattedMessage id="TopbarMobileMenu.viewProfileLink" />
              <span className={css.profileChevron} aria-hidden="true">
                ›
              </span>
            </NamedLink>
          ) : null}
        </div>
      </div>

      <div className={css.content}>
        <div className={css.group}>
          <h2 className={css.groupLabel}>
            <FormattedMessage id="TopbarMobileMenu.marketplaceGroup" />
          </h2>
          <ul className={css.rows}>
            <li className={classNames(css.row, currentPageClass('SearchPage'))}>
              <NamedLink name="SearchPage" className={css.rowLink}>
                <span className={css.rowIcon}>
                  <DrawerHomeIcon />
                </span>
                <FormattedMessage id="TopbarMobileMenu.homeLink" />
              </NamedLink>
            </li>
            <li className={classNames(css.row, currentPageClass('SellerDirectoryPage'))}>
              <NamedLink name="SellerDirectoryPage" className={css.rowLink}>
                <span className={css.rowIcon}>
                  <DrawerDirectoryIcon />
                </span>
                <FormattedMessage id="TopbarMobileMenu.directoryLink" />
              </NamedLink>
            </li>
            <li className={classNames(css.row, currentPageClass('ManageListingsPage'))}>
              <NamedLink name="ManageListingsPage" className={css.rowLink}>
                <span className={css.rowIcon}>
                  <DrawerListingsIcon />
                </span>
                <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
              </NamedLink>
            </li>
            <li className={classNames(css.row, currentPageClass('WatchedListingsPage'))}>
              <NamedLink name="WatchedListingsPage" className={css.rowLink}>
                <span className={css.rowIcon}>
                  <DrawerWatchedIcon />
                </span>
                <FormattedMessage id="TopbarMobileMenu.watchedListingsLink" />
              </NamedLink>
            </li>
            <li className={classNames(css.row, currentPageClass(`TransactionsPage:${inboxTab}`))}>
              <NamedLink name="TransactionsPage" params={{ tab: inboxTab }} className={css.rowLink}>
                <span className={css.rowIcon}>
                  <DrawerTransactionsIcon />
                </span>
                <span className={css.rowLabelWithBadge}>
                  <FormattedMessage id="TopbarMobileMenu.yourTransactionsLink" />
                  {notificationCountBadge}
                </span>
              </NamedLink>
            </li>
            <li className={css.row}>
              <ExternalLink
                href={xenforoDirectMessagesInboxUrl()}
                className={css.rowLink}
                title={forumMessagesLabel}
                aria-label={forumMessagesLabel}
              >
                <span className={css.rowIcon}>
                  <EnvelopeIcon size={18} />
                </span>
                <span className={css.rowLabelWithBadge}>
                  <FormattedMessage id="TopbarMobileMenu.messages" />
                  {forumUnreadCountBadge}
                </span>
              </ExternalLink>
            </li>
          </ul>
        </div>

        <div className={css.group}>
          <h2 className={css.groupLabel}>
            <FormattedMessage id="TopbarMobileMenu.accountGroup" />
          </h2>
          <ul className={css.rows}>
            <li className={classNames(css.row, currentPageClass('ProfileSettingsPage'))}>
              <NamedLink name="ProfileSettingsPage" className={css.rowLink}>
                <span className={css.rowIcon}>
                  <DrawerProfileSettingsIcon />
                </span>
                <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
              </NamedLink>
            </li>
            <li className={classNames(css.row, currentPageClass('AccountSettingsPage'))}>
              <NamedLink name="AccountSettingsPage" className={css.rowLink}>
                <span className={css.rowIcon}>
                  <DrawerAccountSettingsIcon />
                </span>
                <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
              </NamedLink>
            </li>
          </ul>
        </div>

        {customLinksSectionMaybe}
      </div>

      <div className={css.footer}>
        {createListingsLinkMaybe}
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
      </div>
    </div>
  );
};

export default TopbarMobileMenu;
